import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Header } from "components/DesignSystem/Header/Header";
import Tab from "components/DesignSystem/Tab/Tab";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { AllTodos } from "components/TodoList/AllTodos";
import { BooksTodos } from "components/TodoList/BooksTodos";
import { TaxTodos } from "components/TodoList/TaxTodos";
import {
  TODO_TAB_CLICKED_INSIDE_TODO,
  TODOS_PAGE_OPENED,
} from "constants/analyticsEvents";
import { ALL, INKLE_BOOKS, INKLE_TAX } from "constants/home";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useEffect, useState } from "react";
import EmptyActionItem from "static/images/EmptyActionItem.svg";
import {
  TaxActionItemType,
  useGetActionItemsQuery,
} from "store/apis/actionItems";
import { BooksActionItems } from "types/Models/books";
import { TodoListLoader } from "./TodoListLoader";

export const TodoList = () => {
  const [selectedTab, setSelectedTab] = useState<
    typeof INKLE_TAX | typeof INKLE_BOOKS | typeof ALL
  >(ALL);
  const [selectedStatusTab, setSelectedStatusTab] = useState<
    "REQUESTED" | "DEPRECATED" | "SUBMITTED"
  >("REQUESTED");
  const { uuid: groupId } = useCurrentGroupContext();
  const { trackEvent } = useAnalytics();
  const { isAdmin } = useRoleBasedView();

  useEffect(() => {
    trackEvent(TODOS_PAGE_OPENED);
  }, []);

  const {
    data = { tax_action_items: [] },
    isSuccess: isTaxSuccess,
    isLoading: isTaxLoading,
  } = useGetActionItemsQuery(
    {
      groupId: groupId,
      type: "TAX",
      card_status: selectedStatusTab || null,
    },
    {
      skip: !groupId || !selectedStatusTab,
    }
  );

  const {
    data: booksData = { book_action_items: [] },
    isSuccess: isBooksSuccess,
    isLoading: isBooksLoading,
  } = useGetActionItemsQuery(
    {
      groupId: groupId,
      type: "BOOKS",
      card_status: selectedStatusTab || null,
    },
    {
      skip: !groupId || !selectedStatusTab,
    }
  );

  const { action_items_response, channel_url } =
    (booksData as BooksActionItems) || {};

  const actionItems =
    action_items_response?.filter(
      ({ item_type }) => item_type !== "Reconciliation"
    ) || [];

  const allBooksActionItems = actionItems.flatMap(({ items, item_type }) =>
    items.map((item) => ({ ...item, item_type }))
  );

  const totalBooksItemsCount = allBooksActionItems.length;

  const totalTaxItemsCount = (data as { tax_action_items: TaxActionItemType[] })
    ?.tax_action_items?.length;

  const isEmpty = totalTaxItemsCount === 0 && totalBooksItemsCount === 0;

  return (
    <DashboardLayout
      header={
        <div className="t-sticky t-top-0">
          <Header
            v2
            title="Action Centre"
            className="t-border-b-0"
            bottom={
              <div className="t-space-y-4">
                {isAdmin && (
                  <Tab.Root
                    defaultValue="REQUESTED"
                    className="t-h-full t-border t-border-solid t-border-neutral-0 t-border-t-0 t-border-l-0 t-border-r-0"
                  >
                    <Tab.List className="t-flex t-gap-2">
                      <Tab.Trigger
                        value="REQUESTED"
                        onClick={() => setSelectedStatusTab("REQUESTED")}
                      >
                        <span className="t-text-body-sm">Pending</span>
                      </Tab.Trigger>
                      <Tab.Trigger
                        value="SUBMITTED"
                        onClick={() => setSelectedStatusTab("SUBMITTED")}
                      >
                        <span className="t-text-body-sm">Completed</span>
                      </Tab.Trigger>

                      <Tab.Trigger
                        value="DEPRECATED"
                        onClick={() => setSelectedStatusTab("DEPRECATED")}
                      >
                        <span className="t-text-body-sm">Archived</span>
                      </Tab.Trigger>
                    </Tab.List>
                  </Tab.Root>
                )}
                <ToggleGroup
                  value={selectedTab}
                  onValueChange={(value: string) => {
                    if (value) {
                      setSelectedTab(
                        value as
                          | typeof INKLE_TAX
                          | typeof INKLE_BOOKS
                          | typeof ALL
                      );
                    }
                  }}
                >
                  <ToggleGroupItem
                    value={ALL}
                    onClick={() => trackEvent(TODO_TAB_CLICKED_INSIDE_TODO)}
                  >
                    <div className="t-w-[124px] t-flex t-items-center t-justify-center t-gap-1">
                      <div>
                        All ({totalBooksItemsCount + totalTaxItemsCount})
                      </div>
                    </div>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value={INKLE_TAX}
                    onClick={() => trackEvent(TODO_TAB_CLICKED_INSIDE_TODO)}
                  >
                    <div className="t-w-[124px] t-flex t-items-center t-justify-center t-gap-1">
                      <div>Tax ({totalTaxItemsCount})</div>
                    </div>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value={INKLE_BOOKS}
                    onClick={() => trackEvent(TODO_TAB_CLICKED_INSIDE_TODO)}
                  >
                    <div className="t-w-[124px] t-flex t-items-center t-justify-center t-gap-1">
                      <div>Books ({totalBooksItemsCount})</div>
                    </div>
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            }
          />
        </div>
      }
    >
      <Async.Root
        {...{
          isEmpty: isEmpty,
          isLoading:
            isTaxLoading || isBooksLoading || !isTaxSuccess || !isBooksSuccess,
          isSuccess: isTaxSuccess && isBooksSuccess,
          renderOnLoad: <TodoListLoader />,
        }}
      >
        <Async.Empty>
          <div className="t-flex t-items-center t-justify-center t-h-full">
            <img src={EmptyActionItem} alt="noActions" />
          </div>
        </Async.Empty>

        <Async.Success>
          <div className="t-w-full">
            {selectedTab === ALL && (
              <AllTodos selectedStatusTab={selectedStatusTab} />
            )}
            {selectedTab === INKLE_TAX && (
              <TaxTodos selectedStatusTab={selectedStatusTab} />
            )}
            {selectedTab === INKLE_BOOKS && (
              <BooksTodos selectedStatusTab={selectedStatusTab} />
            )}
          </div>
        </Async.Success>
      </Async.Root>
    </DashboardLayout>
  );
};
